/**
 * After core-js + Office WebView: webpack ProvidePlugin maps `Promise` to es6-promise,
 * which does not implement Promise.allSettled (ES2020). docx-preview calls it from renderAsync.
 *
 * Loaded last in the polyfill chunk (webpack.config.js) and imported from taskpane entry
 * so this runs after any chunk that mutates `Promise`.
 */
import ES6PromiseModule from 'es6-promise';

type PromiseCtor = typeof Promise & {
  allSettled?: <T>(
    promises: Iterable<T | PromiseLike<T>>
  ) => Promise<PromiseSettledResult<Awaited<T>>[]>;
};

function patchPromiseAllSettled(ctor: PromiseCtor | undefined): void {
  if (!ctor || typeof ctor.allSettled === 'function') return;
  ctor.allSettled = function <T>(
    promises: Iterable<T | PromiseLike<T>>
  ): Promise<PromiseSettledResult<Awaited<T>>[]> {
    return ctor.all(
      Array.from(promises, (item) =>
        ctor.resolve(item).then(
          (value) => ({ status: 'fulfilled', value } as PromiseFulfilledResult<Awaited<T>>),
          (reason) => ({ status: 'rejected', reason } as PromiseRejectedResult)
        )
      )
    );
  };
}

/** Apply to every global/object form Office + bundler might hand to docx-preview. */
export function ensurePromiseAllSettledPolyfill(): void {
  const ES6PromiseCtor = ES6PromiseModule as unknown as PromiseCtor;
  patchPromiseAllSettled(ES6PromiseCtor);
  patchPromiseAllSettled(Promise as PromiseCtor);
  if (typeof globalThis !== 'undefined' && globalThis.Promise) {
    patchPromiseAllSettled(globalThis.Promise as PromiseCtor);
  }
  if (typeof global !== 'undefined' && (global as { Promise?: PromiseCtor }).Promise) {
    patchPromiseAllSettled((global as { Promise: PromiseCtor }).Promise);
  }
  if (typeof window !== 'undefined' && window.Promise) {
    patchPromiseAllSettled(window.Promise as PromiseCtor);
  }
}

ensurePromiseAllSettledPolyfill();
